import { useCallback, useMemo } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { FileSearchCorner, History } from 'lucide-react';
import z from 'zod';

import { useGetDashboardDataCheckExecutions } from '@/api/gen/data-harmonization-v2-dashboard-data-checks';
import { TLDataCheckType } from '@/api/gen/tessera-backend-api.schemas';
import { DATA_CHECK_DETAIL_TABS_CONFIG } from '@/features/data-harmonization/config/data-check-detail-tabs.config';
import { DataCheckTitle } from '@/features/data-harmonization/datacheck/DataCheckTitle';
import { ErrorDetailTable } from '@/features/data-harmonization/datacheck/error-detail/ErrorDetailTable';
import { ExecutionHistoryTabContent } from '@/features/data-harmonization/datacheck/ExecutionHistoryTabContent';
import { DHTabsLayout } from '@/features/data-harmonization/DHTabsLayout';
import { useDHPermissions } from '@/features/data-harmonization/hooks/useDHPermissions';
import { useEnrichedDataChecksData } from '@/features/data-harmonization/hooks/useEnrichedDataChecksData';
import { useTabsConfig } from '@/features/data-harmonization/hooks/useTabsConfig';
import { DHPageAccessProvider } from '@/features/data-harmonization/providers/DHPageAccessProvider';
import { TLAIDataHarmonizationDataCheckTabType } from '@/features/data-harmonization/types';
import { getWorkflowCheckBinding } from '@/features/data-harmonization/workflow-check';
import type { TLAIDHDataCheckExecution, TLAIDHEnrichedDataCheckRecord } from '@/types/api';
import { Button } from '@/ui/Button';
import { updateUrlParamsWithoutRefresh } from '@/utils/url';

export const Route = createFileRoute(
  '/_protected/t/$tenantId/data-harmonization/dashboard/$dashboardId/executionDetails/$dashboardDataCheckId',
)({
  component: DataHarmonizationDashboardExecutionDetailsRoute,
  validateSearch: z.object({
    tab: z.nativeEnum(TLAIDataHarmonizationDataCheckTabType).optional().catch(undefined),
    filters: z.string().optional(),
    sorts: z.string().optional(),
  }),
});

const DATA_CHECK_DETAIL_PAGE_PERMISSION = 'page:data-checks';

function DataHarmonizationDashboardExecutionDetailsRoute() {
  const { dashboardId, dashboardDataCheckId } = Route.useParams();
  const { tab: tabParam } = Route.useSearch();

  const router = useRouter();
  const { can } = useDHPermissions();
  const { enabledTabs, hasTabAccess, getActiveTab } = useTabsConfig({
    config: DATA_CHECK_DETAIL_TABS_CONFIG,
    defaultTab: TLAIDataHarmonizationDataCheckTabType.ERROR_DETAIL,
    dashboardId,
  });

  const activeTab = getActiveTab(tabParam ?? null);

  const { data: allDataChecks, isLoading: isLoadingAllDataChecks } = useEnrichedDataChecksData({
    dashboardId,
    isEnabled: can('view:data-checks', dashboardId),
  });

  const { data: executions, isLoading: isLoadingExecutions } = useGetDashboardDataCheckExecutions(
    { dashboardDataCheckId },
    undefined,
    { query: { enabled: can('view:execution-history', dashboardId) } },
  );

  const dashboardDataCheck = useMemo(() => {
    return allDataChecks?.find((check) => check.dashboard_data_check_id === dashboardDataCheckId);
  }, [allDataChecks, dashboardDataCheckId]);

  const isFoundryCheck = dashboardDataCheck?.check_metadata?.data_check_type === TLDataCheckType.FOUNDRY;
  const workflowId = getWorkflowCheckBinding(dashboardDataCheck?.check_metadata)?.workflow_id;

  const isLoading = isLoadingAllDataChecks || isLoadingExecutions;

  const handleTabChange = useCallback((value: TLAIDataHarmonizationDataCheckTabType) => {
    updateUrlParamsWithoutRefresh({ tab: value }, { replace: true });
  }, []);

  const latestCompletedExecution = useMemo(() => {
    return executions?.executions
      ?.filter((exec) => exec.status === 'COMPLETED')
      .sort((a, b) => {
        const dateA = new Date(a.completed_date || a.created_date).getTime();
        const dateB = new Date(b.completed_date || b.created_date).getTime();
        return dateB - dateA;
      })[0];
  }, [executions?.executions]);

  return (
    <DHPageAccessProvider
      permission={DATA_CHECK_DETAIL_PAGE_PERMISSION}
      isLoading={isLoading}
      dashboardId={dashboardId}
    >
      {!dashboardDataCheck ? (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <h1 className="text-2xl font-bold text-white">Data Check Not Found</h1>
          <p className="text-gray-400">The data check with ID &quot;{dashboardDataCheckId}&quot; does not exist.</p>
          <Button className="hover:bg-white/10" onClick={() => router.history.back()}>
            Go Back
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <DataCheckTitle dataCheck={dashboardDataCheck as TLAIDHEnrichedDataCheckRecord} dashboardId={dashboardId} />
          </div>

          <DHTabsLayout
            activeTab={activeTab}
            onTabChange={handleTabChange}
            enabledTabs={enabledTabs}
            hasTabAccess={hasTabAccess}
            className="flex-1 gap-4"
          >
            <DHTabsLayout.Content value={TLAIDataHarmonizationDataCheckTabType.ERROR_DETAIL}>
              {latestCompletedExecution ? (
                <ErrorDetailTable
                  execution={latestCompletedExecution as unknown as TLAIDHDataCheckExecution}
                  isLoading={isLoadingExecutions}
                  dataCheckName={dashboardDataCheck?.name}
                  ruleNumber={dashboardDataCheck?.rule_id ?? undefined}
                />
              ) : (
                <div className="flex h-[calc(100vh-600px)] flex-col items-center justify-center gap-4 text-center">
                  <FileSearchCorner className="size-8 text-white opacity-80" />
                  <p className="whitespace-pre-line text-sm text-white/60">
                    {`No Error Detail data is available for this data check.\nPlease check the Execution History to see if the data check has been executed.`}
                  </p>
                  <Button
                    Icon={History}
                    onClick={() => handleTabChange(TLAIDataHarmonizationDataCheckTabType.EXECUTION_HISTORY)}
                  >
                    Go to Executions
                  </Button>
                </div>
              )}
            </DHTabsLayout.Content>
            <DHTabsLayout.Content value={TLAIDataHarmonizationDataCheckTabType.EXECUTION_HISTORY}>
              <ExecutionHistoryTabContent
                executions={(executions?.executions ?? []) as unknown as TLAIDHDataCheckExecution[]}
                isLoading={isLoadingExecutions}
                dashboardDataCheckId={dashboardDataCheckId}
                dashboardId={dashboardId}
                isFoundryCheck={isFoundryCheck}
                workflowId={workflowId}
              />
            </DHTabsLayout.Content>
          </DHTabsLayout>
        </>
      )}
    </DHPageAccessProvider>
  );
}
